import React from 'react';
import FlowSelectBox from './FlowSelectBox';
import SubFlowArguments from './SubFlowArguments';
import SubFlowNameInput from './SubFlowNameInput';
import SubFlowFooter from './SubFlowFooter';
import SubFlowHeading from './SubFlowHeading';

import GraphActions from '../../../../actions/graph';
import translations from '../../../../translations';

import { connect } from 'react-redux';
import { concat, pathOr } from 'ramda';
import { getByID } from '../../../../utils/core';
import { addNotification } from '../../../../actions/reduxActions/notification';

import {
    dismissMapElementConfig,
    fetchMapElement,
    saveSubFlowMapElement,
    setMapElement,
    setMapElementSubflowId,
    setMapElementDeveloperName,
} from '../../../../actions/reduxActions/mapElements';
import { MAP_ELEMENT_TYPES } from '../../../../constants';
import { getFlag } from '../../../../config/flags';
import ConfigLayoutWrapper from '../ConfigLayoutWrapper';

/**
 * @param {String} editingToken
 * @param {String} flowId The ID of the Flow that the subflow map element exists in
 * @param {String} id The ID of the subflow map element (if it already exists)
 * @param {String} x The X coordinate of the subflow map elements position on the canvas
 * @param {String} y The Y coordinate of the subflow map elements position on the canvas
 *
 * @description This components acts as the controller for configuring the subflow
 * map element. It handles fetching the map element metadata from the api (this tells
 * as how the subflow map element is currently configured).
 * It also manages the state of how the subflow arguments are to be rendered.
 * It acts as the root component for all other components necessary for configuring the
 * map element.
 */
export class SubFlowConfiguration extends React.Component {

    state = {

        // To indicate that we are waiting for subflow arguments to be rendered
        subFlowArgumentsLoading: true,

        // Contains all of the subflow arguments to be rendered
        subFlowArguments: [],

        // The initial developer name of the map element, so typing doesn't change the title
        initialMapElementDeveloperName: '',
    }

    componentDidMount() {
        const {
            mapElement,
            tenantId,
            authenticationToken,
            id,
            flowId,
            editingToken,
            x, y,
            groupElementId,
        } = this.props;

        if (mapElement) {

            this.setState({ initialMapElementDeveloperName: mapElement.developerName });

            // If we don't already have our map element fetch it from the api
            // and then add it to the map element store (Redux)
            this.props.fetchMapElement({
                mapElementId: id,
                flowId,
                editingToken,
                tenantId,
                authenticationToken,
            });
        } else {

            // The map element does not currently
            // exist in our Redux state so lets set it
            this.props.setMapElement({
                elementType: this.props.elementType,
                x,
                y,
                developerName: '',
                subflow: null,
                id,
                groupElementId,
            });
        }
    }

    componentWillUnmount() {
        this.setState({ subFlowArguments: [] });
    }

    /**
     * @description Takes the subflow map element metadata in its
     * current state, as well as the sub flow arguments from the
     * components state and dispatches an action that saves the
     * map elements configuration via the api
     */
    onSave = () => {
        const {
            mapElement,
            saveSubFlowMapElement: save,
            flowId,
            editingToken,
            tenantId,
            authenticationToken,
            cancel,
        } = this.props;

        if (mapElement) {
            save(
                mapElement,
                flowId,
                editingToken,
                tenantId,
                authenticationToken,
                cancel,
                this.state.subFlowArguments,
            );
        }
    };

    /**
     * @param event The event object passed from the Selectize component
     * @description Is executed everytime the Flow specified
     * as the subflow is changed via the FlowSelectBox component
     */
    onSubflowChange = (event) => {
        const {
            mapElement,
            setMapElementSubflowId: updateSubflow,
        } = this.props;

        const subflowId = pathOr(null, ['value'], event);

        if (event && subflowId) {

            if ((mapElement.subflow && mapElement.subflow.flowId.id !== subflowId) || !mapElement.subflow) {

                // We need to do some cleanup etc in the component state
                // before we update the subflow map element in the Redux store
                this.setState({ subFlowArguments: [], subFlowArgumentsLoading: true }, () => {
                    updateSubflow({
                        mapElement,
                        subflowId,
                    });
                });
            }

        } else {

            // The selection has been cleared
            this.setState({ subFlowArguments: [], subFlowArgumentsLoading: true }, () => {
                updateSubflow({
                    mapElement,
                    subflowId: null,
                });
            });
        }
    };

    /**
     * @param target The target object passed from the name input field
     * @description Updates the name of the subflow map element
     */
    onNameChange = ({ target }) => {
        const {
            mapElement,
            setMapElementDeveloperName: updateDeveloperName,
        } = this.props;

        const developerName = target.value;
        updateDeveloperName({ mapElement, developerName });
    };

    /**
     * @description Gets triggered when the subflow configuration
     * is either saved, or cancelled.
     */
    onClose = () => {
        const {
            flowId,
            tenantId,
            authenticationToken,
            cancel,
        } = this.props;

        // This triggers a re-render of the entire Flow canvas
        // so that changes we have made to the map element appear in the canvas
        GraphActions.get(flowId, tenantId, authenticationToken);

        // Closes the config panel
        cancel(flowId);
    };

    /**
     * @param {Array} subFlowArguments Represents a list of subflow arguments to be displayed
     * @description This just sets subflow arguments in component state. Arguments are
     * concatenated as there are 3 different sets of arguments being requested asynchronously -
     * arguments of type access type "input", "output" and "input_output"
     */
    setSubFlowArguments = (subFlowArguments) => {
        const existingArguments = this.state.subFlowArguments;
        this.setState({ subFlowArguments: concat(subFlowArguments, existingArguments), subFlowArgumentsLoading: false });
    };

    /**
     * @param {Array} subFlowArguments Represents a list of subflow arguments to be displayed
     * @param {Number} matches The number of value matches found
     * @description The actual arguments sent to the subflow will at this point have been automatically matched
     * to the formal arguments defined by the selected subflow. So we just want to set the updated arguments
     * in state.
     */
    setMatchedSubFlowArguments = (subFlowArguments, matches) => {
        if (matches === 0) {
            this.props.addNotification({
                type: 'error',
                message: translations.SF_no_matching_values_text,
                isPersistent: true,
            });
        }
        this.setState({ subFlowArguments, subFlowArgumentsLoading: false });
    };

    /**
     * @param {Boolean} isOpen Should the value picker modal be displayed
     * @param {String} id The ID of the subflow argument that we are representing in component state
     * @description Determine whether a value picker modal should be displayed for manually
     * selecting the value to pass into/ out of the subflow
     */
    togglePicker = (isOpen, id) => {
        const subFlowArguments = this.state.subFlowArguments.map((subFlowArgument) => {
            if (subFlowArgument.id === id) {
                return {
                    ...subFlowArgument,
                    isOpen,
                };
            }
            return subFlowArgument;
        });

        this.setState({ subFlowArguments });
    }

    /**
     * @param {Object} selectedValueMeta Metadata that represents the value that has been selected to pass
     * to the specified argument
     * @param {String} id The ID of the argument representation in component state
     * @description Sets the selected value for the specified subflow argument
     */
    selectArgument = (selectedValueMeta, id) => {
        const subFlowArguments = this.state.subFlowArguments.map((subFlowArgument) => {
            if (subFlowArgument.id === id) {
                return {
                    ...subFlowArgument,
                    isOpen: false,
                    selectedValueMeta,
                };
            }
            return subFlowArgument;
        });

        this.setState({ subFlowArguments });
    }

    render() {
        const {
            allFlows,
            flowId,
            mapElement,
        } = this.props;

        const selectedDeveloperName = pathOr(
            '',
            ['developerName'],
            mapElement,
        );

        const selectedSubFlowId = pathOr(
            '',
            ['subflow', 'flowId', 'id'],
            mapElement,
        );

        const selectedSubflow = allFlows.find(flow => flow.id.id === selectedSubFlowId);
        const selectedSubFlowName = pathOr(
            '',
            ['developerName'],
            selectedSubflow,
        );

        const selectedSubFlowArguments = pathOr(
            '',
            ['subflow', 'arguments'],
            mapElement,
        );

        const renderBody = () => (<>
            <SubFlowNameInput
                onChange={this.onNameChange}
                value={selectedDeveloperName}
            />
            <FlowSelectBox
                onChange={this.onSubflowChange}
                selectedItem={selectedSubFlowName}
                flowId={flowId}
            />
            {selectedSubFlowId ? (
                <SubFlowArguments
                    flowId={flowId}
                    subFlowId={selectedSubFlowId}
                    subFlowArguments={selectedSubFlowArguments}
                    subFlowArgumentsToBeRendered={this.state.subFlowArguments}
                    setSubFlowArguments={this.setSubFlowArguments}
                    onSelect={this.selectArgument}
                    togglePicker={this.togglePicker}
                    isLoading={this.state.subFlowArgumentsLoading}
                    onMatch={this.setMatchedSubFlowArguments}
                />
            ) : null}
        </>);

        const renderFooter = () => (
            <SubFlowFooter
                onClose={this.onClose}
                onSave={this.onSave}
                saveText={getFlag('canvas') ? translations.GRAPH_config_panel_save : translations['subflow save']}
                cancelText={getFlag('canvas') ? translations.GRAPH_config_panel_cancel : translations['subflow cancel']}
            />
        );

        return (
            <ConfigLayoutWrapper
                renderHeader={() => (
                    <SubFlowHeading
                        headingTitle={translations['subflow heading']}
                        headingSubTitle={selectedDeveloperName}
                    />
                )}
                renderBody={renderBody}
                renderFooter={renderFooter}
                title={this.state.initialMapElementDeveloperName}
                elementType={MAP_ELEMENT_TYPES.subflow}
                onHide={this.onClose}
                container={this.props.container}
                className="subflow-config"
            />
        );
    }
}

export default connect(
    ({ flows, currentUser, mapElements }, ownProps) => ({

        // The metadata of the map element to be configured
        mapElement: getByID(ownProps.id, mapElements),

        // All the Flows in the Redux store, we need these
        // for getting more info about the Flow that has been
        // selected as the subflow e.g. name etc (the map element metadata
        // only provides the subflow ID).
        allFlows: flows.all,
        tenantId: currentUser.tenantId,
        authenticationToken: currentUser.authenticationToken,
    }),
    {
        addNotification,
        fetchMapElement,
        saveSubFlowMapElement,
        setMapElement,
        setMapElementSubflowId,
        setMapElementDeveloperName,
        cancel: dismissMapElementConfig,
    },
)(SubFlowConfiguration);
